import React, { useState } from "react";

import Engine from "@vero/expression-core-node/engine/engine";
import { Button as AntButton, Alert } from "antd";

import {
  usePreviewV1Expressions,
  useDeployV1Expressions,
} from "manager/hooks/api/dealSetupQueries";
import Icon from "shared/components/Icon";
import { Modal } from "shared/components/Modal";
import Well from "shared/components/Well";
import useModal from "shared/hooks/useModal";
import { useVeroFlag } from "shared/utils/featureFlags";
import { useSetVeroFlagLocation } from "shared/utils/growthbook";
import { notifyError } from "shared/utils/ui";
import { VeroFlag } from "shared/utils/veroFlags";

interface Workflow {
  id: number;
  identifier?: string;
  expression?: {
    formula?: string;
  };
}

interface LegacyWorkflowInfo {
  type: string;
  displayName: string;
  hasLegacy: boolean;
}

interface V1ExpressionMigrationProps {
  dealSetupId?: number;
  prescreeningDealSetupId?: number;
  location?: string;
  onStageExpressions: (expressions: { [key: string]: string }) => void;
  onRevert: () => void;
  isStaged: boolean;
  workflows: Workflow[];
  onLegacyWorkflowsDetected?: (legacyTypes: string[]) => void;
}

interface PreviewData {
  applicant?: string;
  occupant?: string;
  guarantor?: string;
  lead?: string;
}

interface ConfirmDeployModalProps {
  impactedExpressions: string[];
  onConfirm: () => Promise<void>;
  isDeploying: boolean;
}

const engine = new Engine(true);

const ConfirmDeployModal: React.FC<ConfirmDeployModalProps> = ({
  impactedExpressions,
  onConfirm,
  isDeploying,
}) => {
  const { selfApi } = useModal();

  const handleSubmit = async () => {
    await onConfirm();
    selfApi.closeDialog();
  };

  return (
    <Modal
      title="Confirm V1 Expression Deployment"
      submitButtonLabel="Yes, Deploy All"
      cancelLinkLabel="Cancel"
      submit={handleSubmit}
      showSubmitButton
      showCancelLink
      submitting={isDeploying}
    >
      <Modal.Body>
        <div style={{ marginBottom: 16 }}>
          <p>Are you sure you want to deploy V1 expressions?</p>
          <p>
            <strong>The following expressions will be updated:</strong>
          </p>
          <ul>
            {impactedExpressions.map((type) => (
              <li key={type}>{type} Workflow</li>
            ))}
          </ul>
        </div>

        <Well>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ flexShrink: 0 }}>
              <Icon.OrangeExclamation />
            </div>
            <p style={{ margin: 0 }}>
              If you don&apos;t want to deploy all at once, click Cancel, then
              either revert the staged changes or make your edits and save each
              workflow individually.
            </p>
          </div>
        </Well>
      </Modal.Body>
    </Modal>
  );
};

/**
 * V1 Expression Migration Component
 *
 * Displays when v1 expression engine is enabled but workflows are still using
 * legacy expression format (without $group structures).
 *
 * Provides:
 * - Preview button to stage v1 expressions in the editors
 * - Revert button to undo staged changes
 * - Deploy All button to save all v1 expressions at once
 * - Confirmation modal before deploying
 */
const V1ExpressionMigration: React.FC<V1ExpressionMigrationProps> = ({
  dealSetupId,
  prescreeningDealSetupId,
  location,
  onStageExpressions,
  onRevert,
  isStaged,
  workflows,
  onLegacyWorkflowsDetected,
}) => {
  useSetVeroFlagLocation(location);

  const isV1ExpressionEngineEnabled = useVeroFlag(VeroFlag.useV1ExpressionEngine);
  const isV1IncomeFlowEnabled = useVeroFlag(VeroFlag.useV1IncomeFlow);
  // V1 expression engine is enabled if either flag is on (income flow depends on expression engine)
  const isV1Enabled = isV1ExpressionEngineEnabled || isV1IncomeFlowEnabled;
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const { openModalDialog } = useModal();

  type V1ExpressionType = "applicant" | "occupant" | "guarantor" | "lead";

  const [previewMutation, { isLoading: isPreviewing }] =
    usePreviewV1Expressions() as [
      (params: {
        id: number;
        types: V1ExpressionType[];
      }) => Promise<PreviewData>,
      { isLoading: boolean }
    ];

  const { deployV1Expressions, isDeployV1ExpressionsLoading: isDeploying } =
    useDeployV1Expressions() as {
      deployV1Expressions: (params: {
        id: number;
        types: V1ExpressionType[];
      }) => Promise<unknown>;
      isDeployV1ExpressionsLoading: boolean;
    };

  // Determine which workflow types have legacy expressions (missing $group)
  const getWorkflowType = (identifier?: string): string => {
    const id = identifier?.toLowerCase() || "";
    if (id.includes("applicant")) return "applicant";
    if (id.includes("occupant")) return "occupant";
    if (id.includes("guarantor")) return "guarantor";
    if (id.includes("lead")) return "lead";
    return "";
  };

  const legacyWorkflows: LegacyWorkflowInfo[] = React.useMemo(() => {
    const workflowTypes = [
      { type: "applicant", displayName: "Applicant" },
      { type: "occupant", displayName: "Occupant" },
      { type: "guarantor", displayName: "Guarantor" },
      { type: "lead", displayName: "Lead" },
    ];

    return workflowTypes.map(({ type, displayName }) => {
      const workflow = workflows?.find(
        (w) => getWorkflowType(w.identifier) === type
      );
      const formula = workflow?.expression?.formula || "";
      const hasLegacy = formula.length > 0 && !formula.includes("$group");
      return { type, displayName, hasLegacy };
    });
  }, [workflows]);

  const legacyWorkflowTypes = legacyWorkflows
    .filter((w) => w.hasLegacy)
    .map((w) => w.type);

  const legacyWorkflowDisplayNames = legacyWorkflows
    .filter((w) => w.hasLegacy)
    .map((w) => w.displayName);

  const hasLegacyExpressions = legacyWorkflowTypes.length > 0;

  React.useEffect(() => {
    if (onLegacyWorkflowsDetected) {
      onLegacyWorkflowsDetected(legacyWorkflowTypes);
    }
  }, [legacyWorkflowTypes.join(","), onLegacyWorkflowsDetected]);

  const hasScreeningDealSetup = !!dealSetupId;
  const hasPrescreeningDealSetup = !!prescreeningDealSetupId;

  if (
    !isV1Enabled ||
    !hasLegacyExpressions ||
    (!hasScreeningDealSetup && !hasPrescreeningDealSetup)
  ) {
    return null;
  }

  const handlePreview = async () => {
    try {
      const screeningTypes = legacyWorkflowTypes.filter((t) =>
        ["applicant", "occupant", "guarantor"].includes(t)
      ) as V1ExpressionType[];
      const leadTypes = legacyWorkflowTypes.filter(
        (t) => t === "lead"
      ) as V1ExpressionType[];

      let data: PreviewData = {};

      if (hasScreeningDealSetup && screeningTypes.length > 0 && dealSetupId) {
        const screeningData = await previewMutation({
          id: dealSetupId,
          types: screeningTypes,
        });
        data = { ...data, ...screeningData };
      }

      if (
        hasPrescreeningDealSetup &&
        leadTypes.length > 0 &&
        prescreeningDealSetupId
      ) {
        const leadData = await previewMutation({
          id: prescreeningDealSetupId,
          types: leadTypes,
        });
        data = { ...data, ...leadData };
      }

      setPreviewData(data);

      // Validate and format expressions before staging - only for legacy workflows
      const expressions: { [key: string]: string } = {};
      const errors: string[] = [];

      if (data.applicant && legacyWorkflowTypes.includes("applicant")) {
        try {
          expressions.applicant = engine.format(data.applicant);
        } catch (error) {
          errors.push(
            `Applicant: ${
              error instanceof Error ? error.message : String(error)
            }`
          );
        }
      }
      if (data.occupant && legacyWorkflowTypes.includes("occupant")) {
        try {
          expressions.occupant = engine.format(data.occupant);
        } catch (error) {
          errors.push(
            `Occupant: ${
              error instanceof Error ? error.message : String(error)
            }`
          );
        }
      }
      if (data.guarantor && legacyWorkflowTypes.includes("guarantor")) {
        try {
          expressions.guarantor = engine.format(data.guarantor);
        } catch (error) {
          errors.push(
            `Guarantor: ${
              error instanceof Error ? error.message : String(error)
            }`
          );
        }
      }
      if (data.lead && legacyWorkflowTypes.includes("lead")) {
        try {
          expressions.lead = engine.format(data.lead);
        } catch (error) {
          errors.push(
            `Lead: ${error instanceof Error ? error.message : String(error)}`
          );
        }
      }

      if (errors.length > 0) {
        notifyError(
          <div>
            <div>V1 Expression preview generated invalid expressions:</div>
            {errors.map((err) => (
              <div key={err}>{err}</div>
            ))}
          </div>
        );
        setPreviewData(null);
        return;
      }

      onStageExpressions(expressions);
    } catch (error) {
      // Error is handled by the mutation's onError
    }
  };

  const handleRevert = () => {
    setPreviewData(null);
    onRevert();
  };

  const getImpactedExpressions = () => {
    // Only show expressions that are both staged and were legacy
    return legacyWorkflowDisplayNames.filter((displayName) => {
      const type = displayName.toLowerCase();
      return previewData?.[type as keyof PreviewData];
    });
  };

  const handleConfirmDeploy = async () => {
    const screeningTypes = legacyWorkflowTypes.filter((t) =>
      ["applicant", "occupant", "guarantor"].includes(t)
    ) as V1ExpressionType[];
    const leadTypes = legacyWorkflowTypes.filter(
      (t) => t === "lead"
    ) as V1ExpressionType[];

    if (hasScreeningDealSetup && screeningTypes.length > 0 && dealSetupId) {
      await deployV1Expressions({
        id: dealSetupId,
        types: screeningTypes,
      });
    }

    if (
      hasPrescreeningDealSetup &&
      leadTypes.length > 0 &&
      previewData?.lead &&
      prescreeningDealSetupId
    ) {
      await deployV1Expressions({
        id: prescreeningDealSetupId,
        types: leadTypes,
      });
    }

    setPreviewData(null);
    onRevert();
  };

  const handleDeployClick = () => {
    openModalDialog(ConfirmDeployModal, {
      impactedExpressions: getImpactedExpressions(),
      onConfirm: handleConfirmDeploy,
      isDeploying,
    });
  };

  return (
    <div className="vega v1-expression-migration" style={{ marginBottom: 16 }}>
      <Alert
        type="success"
        message="V1 Expression Engine Available"
        description={
          <>
            <p style={{ margin: 0 }}>
              The following workflows have legacy expressions that can be
              upgraded to V1 format with grouped tasks:
            </p>
            <ul style={{ margin: "8px 0", paddingLeft: 20 }}>
              {legacyWorkflowDisplayNames.map((name) => (
                <li key={name}>{name} Workflow</li>
              ))}
            </ul>
            {isStaged && (
              <strong style={{ display: "block", marginTop: 8 }}>
                Preview is active - review the staged expressions below, then
                deploy or revert.
              </strong>
            )}
            <div
              className="migration-actions"
              style={{ display: "flex", gap: 8, marginTop: "16px" }}
            >
              {!isStaged ? (
                <AntButton
                  type="primary"
                  onClick={handlePreview}
                  loading={isPreviewing}
                >
                  Preview V1 Expressions
                </AntButton>
              ) : (
                <>
                  <AntButton onClick={handleRevert}>Exit Preview</AntButton>
                  <AntButton
                    type="primary"
                    onClick={handleDeployClick}
                    loading={isDeploying}
                  >
                    Deploy V1 Expressions
                  </AntButton>
                </>
              )}
            </div>
          </>
        }
        showIcon
        style={{ marginBottom: 16 }}
      />
    </div>
  );
};

export default V1ExpressionMigration;
